<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import WuiButton from 'dashboard/components-next/button/Button.vue';
import EventDrawer from './EventDrawer.vue';
import CalendarPopover from './CalendarPopover.vue';
import CalendarSettingsModal from './CalendarSettingsModal.vue';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfDay,
  endOfDay,
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

export default {
  name: 'CalendarView',
  components: {
    WuiButton,
    EventDrawer,
    CalendarPopover,
    CalendarSettingsModal,
  },
  data() {
    return {
      currentDate: new Date(),
      viewMode: 'month',
      selectedEvent: null,
      hours: Array.from({ length: 24 }, (_, i) => i),
      currentTime: new Date(),
      timer: null,
      showEventDrawer: false,
      editEventData: null,
      isSavingEvent: false,
      showCalendarPopover: false,
      showSettingsModal: false,
      settingsTab: 'appearance',
      isMobileViewport: false,
      showCancelledEvents: false,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      allCalendars: 'calendar/getCalendars',
      allEvents: 'calendar/getEvents',
      selectedCalendarIds: 'calendar/getSelectedCalendarIds',
      connectedAccounts: 'calendar/getConnectedAccounts',
      isSyncing: 'calendar/getIsSyncing',
      colorMode: 'calendar/getColorMode',
      selectedCount: 'calendar/getSelectedCount',
      defaultCalendarId: 'calendar/getDefaultCalendarId',
      canManageSettings: 'calendar/getCanManageSettings',
      creatableCalendarIds: 'calendar/getCreatableCalendarIds',
      reauthRequiredHookIds: 'calendar/getReauthRequiredHookIds',
      syncCompletedAt: 'calendar/getSyncCompletedAt',
      allInboxes: 'inboxes/getInboxes',
    }),
    reauthAccounts() {
      return (this.connectedAccounts || []).filter(a =>
        this.reauthRequiredHookIds.includes(a.id)
      );
    },
    currentPeriodLabel() {
      if (this.viewMode === 'month') {
        return format(this.currentDate, 'MMMM yyyy', { locale: ptBR });
      }
      if (this.viewMode === 'week') {
        const start = startOfWeek(this.currentDate, { locale: ptBR });
        const end = addDays(start, 6);
        return `${format(start, 'd')} - ${format(end, "d 'de' MMMM yyyy", { locale: ptBR })}`;
      }
      return format(this.currentDate, "d 'de' MMMM yyyy", { locale: ptBR });
    },
    monthDays() {
      const start = startOfWeek(startOfMonth(this.currentDate));
      const end = endOfWeek(endOfMonth(this.currentDate));
      const days = [];
      let day = start;

      while (day <= end || days.length < 35) {
        days.push({
          date: day,
          isCurrentMonth: isSameMonth(day, this.currentDate),
          isToday: isSameDay(day, new Date()),
          events: this.getEventsForDate(day),
        });
        day = addDays(day, 1);
      }
      return days;
    },
    weekDates() {
      const start = startOfWeek(this.currentDate);
      const days = [];
      for (let i = 0; i < 7; i += 1) {
        const d = addDays(start, i);
        days.push({
          date: d,
          label: this.weekDayLabels[i],
          isToday: isSameDay(d, new Date()),
        });
      }
      return days;
    },
    viewOptions() {
      return [
        { id: 'month', label: this.$t('CALENDAR.VIEWS.MONTH') },
        { id: 'week', label: this.$t('CALENDAR.VIEWS.WEEK') },
        { id: 'day', label: this.$t('CALENDAR.VIEWS.DAY') },
      ];
    },
    weekDayLabels() {
      return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d =>
        this.$t(`CALENDAR.WEEKDAYS.${d}`)
      );
    },
    canCreateEvents() {
      return (this.creatableCalendarIds || []).length > 0;
    },
    defaultCreatableCalendarId() {
      const creatable = this.creatableCalendarIds || [];
      if (!creatable.length) return null;
      if (
        this.defaultCalendarId &&
        creatable.includes(this.defaultCalendarId)
      ) {
        return this.defaultCalendarId;
      }
      const selectedCreatable = (this.selectedCalendarIds || []).find(id =>
        creatable.includes(id)
      );
      return selectedCreatable || creatable[0];
    },
  },
  watch: {
    currentDate() {
      this.fetchEventsWithRange();
    },
    viewMode() {
      this.fetchEventsWithRange();
    },
    syncCompletedAt(val) {
      if (!val) return;
      this.fetchEventsWithRange();
      useAlert(this.$t('CALENDAR.SYNC_SUCCESS'));
    },
  },
  mounted() {
    const currentUser = this.$store.getters.getCurrentUser;
    const currentAccountId = this.$store.getters.getCurrentAccountId;
    const accountData = (currentUser?.accounts || []).find(
      a => Number(a.id) === Number(currentAccountId)
    );
    const userPerms = accountData?.permissions || [];
    if (
      !userPerms.includes('administrator') &&
      !userPerms.includes('calendar.view')
    ) {
      this.$router.replace({ name: 'home' });
      return;
    }
    this.applyResponsiveView();
    window.addEventListener('resize', this.applyResponsiveView);
    this.fetchData();
    this.$store.dispatch('calendar/fetchConnectedAccounts');
    this.$store.dispatch('inboxes/get');
    document.addEventListener('click', this.closeCalendarPopover);
    this.timer = setInterval(() => {
      this.currentTime = new Date();
    }, 60000);

    // If redirected from Google OAuth callback, a sync job was enqueued — show skeleton
    // Replace URL immediately to avoid re-triggering on page refresh
    if (this.$route?.query?.sync === 'success') {
      this.$router.replace({ query: {} });
      this.$store.commit('calendar/SET_SYNCING', true);
      const t = setTimeout(
        () => this.$store.commit('calendar/SET_SYNCING', false),
        90000
      );
      this.$store.commit('calendar/SET_SYNC_TIMEOUT', t);
    }
  },
  beforeUnmount() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.applyResponsiveView);
    document.removeEventListener('click', this.closeCalendarPopover);
  },
  methods: {
    ...mapActions({
      fetchCalendars: 'calendar/fetchCalendars',
      fetchEvents: 'calendar/fetchEvents',
      toggleCalendarSelection: 'calendar/toggleCalendarSelection',
      syncAccount: 'calendar/syncAccount',
    }),
    closeCalendarPopover(e) {
      if (
        this.$refs.calendarPopoverAnchor &&
        !this.$refs.calendarPopoverAnchor.contains(e.target)
      ) {
        this.showCalendarPopover = false;
      }
    },
    applyResponsiveView() {
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const wasMobile = this.isMobileViewport;
      this.isMobileViewport = mobile;
      if (mobile && !wasMobile && this.viewMode === 'month') {
        this.viewMode = 'day';
      }
    },
    onToggleCalendar(calendarId) {
      this.toggleCalendarSelection(calendarId);
    },
    async triggerSync() {
      await this.$store.dispatch('calendar/syncAll');
    },
    openSettings(tab = 'appearance') {
      this.settingsTab = tab;
      this.showSettingsModal = true;
      this.showCalendarPopover = false;
    },
    async onSyncAccount(hookId) {
      this.showSettingsModal = false;
      try {
        await this.syncAccount(hookId);
        useAlert(this.$t('CALENDAR.SYNC_ACCOUNT_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CALENDAR.SYNC_ERROR'));
      }
    },
    async onDisconnectAccount(hookId, done) {
      try {
        await this.$store.dispatch('calendar/disconnectAccount', hookId);
        this.$store.dispatch('calendar/clearReauthRequired', hookId);
        useAlert(this.$t('CALENDAR.DISCONNECT_SUCCESS'));
        await this.$store.dispatch('calendar/initializeDefaultCalendar');
      } catch (error) {
        useAlert(this.$t('CALENDAR.DISCONNECT_ERROR') || 'Erro ao desconectar conta. Tente novamente.');
      } finally {
        if (typeof done === 'function') done();
      }
    },
    onColorModeChange(mode) {
      this.$store.dispatch('calendar/setColorMode', mode);
    },
    onDefaultCalendarChange(calendarId) {
      this.$store.dispatch('calendar/setDefaultCalendar', calendarId);
      useAlert(this.$t('CALENDAR.DEFAULT_UPDATED'));
    },
    onCalendarUpdated(calendar) {
      this.$store.commit('calendar/UPDATE_CALENDAR', calendar);
    },
    async fetchData() {
      try {
        await this.fetchCalendars();
        await this.fetchEventsWithRange();
      } catch (error) {
        useAlert(this.$t('CALENDAR.LOAD_ERROR'));
      }
    },
    async fetchEventsWithRange() {
      let start;
      let end;
      if (this.viewMode === 'month') {
        start = startOfWeek(startOfMonth(this.currentDate));
        end = endOfWeek(endOfMonth(this.currentDate));
      } else if (this.viewMode === 'week') {
        start = startOfWeek(this.currentDate);
        end = endOfWeek(this.currentDate);
      } else {
        start = startOfDay(this.currentDate);
        end = endOfDay(this.currentDate);
      }

      await this.fetchEvents({
        start: start.toISOString(),
        end: end.toISOString(),
        force: true,
      });
    },
    shiftPeriod(amount) {
      if (this.viewMode === 'month') {
        this.currentDate =
          amount > 0
            ? addMonths(this.currentDate, 1)
            : subMonths(this.currentDate, 1);
      } else if (this.viewMode === 'week') {
        this.currentDate = addDays(this.currentDate, amount * 7);
      } else {
        this.currentDate = addDays(this.currentDate, amount);
      }
    },
    goToToday() {
      this.currentDate = new Date();
    },
    getEventStyle(event) {
      const start = new Date(event.start_at);
      const end = new Date(event.end_at);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const durationMinutes = (end - start) / (1000 * 60);

      const colIndex = event.colIndex || 0;
      const localCols = event.localCols || 1;

      // Tighter staggered look: leftOffset depends on colIndex and local density
      const leftOffset = (colIndex / localCols) * 85;
      const width = 95 - leftOffset; // Leaves 5% gap on the right

      return {
        top: `${(startMinutes / 60) * 80}px`,
        height: `${Math.max((durationMinutes / 60) * 80, 20)}px`,
        ...this.eventCardStyle(event.calendar_id),
        borderColor: this.eventAccentColor(event.calendar_id),
        position: 'absolute',
        zIndex: 10 + colIndex,
        left: `${leftOffset}%`,
        width: `${width}%`,
      };
    },
    getEventStyle_Day(event) {
      const start = new Date(event.start_at);
      const end = new Date(event.end_at);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const durationMinutes = (end - start) / (1000 * 60);

      const colIndex = event.colIndex || 0;
      const localCols = event.localCols || 1;

      // Staggered stacking for day view
      const leftOffset = (colIndex / localCols) * 80;
      const width = 95 - leftOffset; // Leaves 5% gap on the right

      return {
        top: `${(startMinutes / 60) * 100}px`,
        height: `${Math.max((durationMinutes / 60) * 100, 40)}px`,
        ...this.eventCardStyle(event.calendar_id),
        borderColor: this.eventAccentColor(event.calendar_id),
        position: 'absolute',
        zIndex: 10 + colIndex,
        left: `${leftOffset + 2}%`, // Extra padding
        width: `${width - 4}%`,
      };
    },
    getCurrentTimeTop() {
      const minutes =
        this.currentTime.getHours() * 60 + this.currentTime.getMinutes();
      return (minutes / 60) * 80;
    },
    getCurrentTimeTop_Day() {
      const minutes =
        this.currentTime.getHours() * 60 + this.currentTime.getMinutes();
      return (minutes / 60) * 100;
    },
    formatHour(hour) {
      if (hour === 0) return '12 am';
      if (hour < 12) return `${hour} am`;
      if (hour === 12) return '12 pm';
      return `${hour - 12} pm`;
    },
    isToday(date) {
      return isSameDay(new Date(date), new Date());
    },
    isAllDayEvent(event) {
      const start = new Date(event.start_at);
      const end = new Date(event.end_at);
      return end - start >= 24 * 60 * 60 * 1000;
    },
    getAllDayEventsForDate(date) {
      return (this.allEvents || [])
        .filter(e => isSameDay(new Date(e.start_at), date))
        .filter(e => (this.selectedCalendarIds || []).includes(e.calendar_id))
        .filter(e => this.showCancelledEvents || e.status !== 'cancelled')
        .filter(e => this.isAllDayEvent(e));
    },
    getEventsForDate(date) {
      let dayEvents = (this.allEvents || [])
        .filter(event => {
          const eventDate = new Date(event.start_at);
          return isSameDay(eventDate, date);
        })
        .filter(event =>
          (this.selectedCalendarIds || []).includes(event.calendar_id)
        )
        .filter(
          event => this.showCancelledEvents || event.status !== 'cancelled'
        );

      // Add temporary event if drawer is open for creation
      if (
        this.showEventDrawer &&
        this.editEventData &&
        !this.editEventData.id
      ) {
        if (isSameDay(new Date(this.editEventData.start_at), date)) {
          dayEvents.push({
            ...this.editEventData,
            id: 'temp',
            isTemp: true,
          });
        }
      }

      dayEvents.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

      if (this.viewMode === 'month') return dayEvents;

      // In week/day views, all-day events are shown in the dedicated all-day row — exclude from time grid
      dayEvents = dayEvents.filter(e => !this.isAllDayEvent(e));

      // Overlap algorithm for Day/Week views

      const clusters = [];
      dayEvents.forEach(event => {
        let added = false;
        clusters.forEach(cluster => {
          if (
            !added &&
            cluster.some(e => {
              const eStart = new Date(e.start_at);
              const eEnd = new Date(e.end_at);
              const eventStart = new Date(event.start_at);
              const eventEnd = new Date(event.end_at);
              return eventStart < eEnd && eventEnd > eStart;
            })
          ) {
            cluster.push(event);
            added = true;
          }
        });
        if (!added) clusters.push([event]);
      });

      clusters.forEach(cluster => {
        const columns = [];
        cluster.forEach(event => {
          let colIndex = columns.findIndex(col => {
            const lastEvent = col[col.length - 1];
            return new Date(event.start_at) >= new Date(lastEvent.end_at);
          });
          if (colIndex === -1) {
            columns.push([event]);
            colIndex = columns.length - 1;
          } else {
            columns[colIndex].push(event);
          }
          event.colIndex = colIndex;
        });

        // Calculate max local overlap for each event in the cluster
        cluster.forEach(event => {
          const eStart = new Date(event.start_at);
          const eEnd = new Date(event.end_at);

          // Find all events in the cluster that overlap with this specific event
          const overlaps = cluster.filter(other => {
            const oStart = new Date(other.start_at);
            const oEnd = new Date(other.end_at);
            return eStart < oEnd && eEnd > oStart;
          });

          // The number of columns needed locally is the number of distinct colIndices among overlapping events
          const localCols = new Set(overlaps.map(o => o.colIndex)).size;
          event.localCols = localCols;
          event.totalCols = columns.length; // Still useful for cluster-wide context
        });
      });

      return dayEvents;
    },
    isSameDay(d1, d2) {
      return isSameDay(d1, d2);
    },
    format(date, pattern, options) {
      return format(date, pattern, options);
    },
    formatTime(date) {
      return format(new Date(date), 'HH:mm');
    },
    formatFullDateTime(start, end) {
      const s = new Date(start);
      const e = new Date(end);
      return `${format(s, "dd 'de' MMMM", { locale: ptBR })} • ${format(s, 'HH:mm')} - ${format(e, 'HH:mm')}`;
    },
    calendarColor(calendarId) {
      const cal = (this.allCalendars || []).find(c => c.id === calendarId);
      return cal?.color || '#3b82f6';
    },
    hexToMuted(hex) {
      // Convert hex to a solid muted version (no transparency)
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      // Mix with white to get a softer but still readable color
      const mix = 0.7; // 70% white
      const mr = Math.round(r + (255 - r) * mix);
      const mg = Math.round(g + (255 - g) * mix);
      const mb = Math.round(b + (255 - b) * mix);
      return `rgb(${mr}, ${mg}, ${mb})`;
    },
    darkenHex(hex, factor = 0.22) {
      const h = (hex || '#3b82f6').replace('#', '');
      const normalized =
        h.length === 3
          ? h
              .split('')
              .map(ch => ch + ch)
              .join('')
          : h;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      const dr = Math.max(0, Math.round(r * (1 - factor)));
      const dg = Math.max(0, Math.round(g * (1 - factor)));
      const db = Math.max(0, Math.round(b * (1 - factor)));
      return `rgb(${dr}, ${dg}, ${db})`;
    },
    eventStatusDot(status) {
      const map = {
        confirmed: 'bg-green-500',
        rescheduled: 'bg-yellow-500',
        cancelled: 'bg-red-500',
      };
      return map[status] || '';
    },
    eventStatusBadgeClass(status) {
      const map = {
        confirmed: 'bg-green-100 text-green-800',
        rescheduled: 'bg-yellow-100 text-yellow-800',
        cancelled: 'bg-red-100 text-red-700 line-through',
      };
      return map[status] || '';
    },
    eventStatusLabel(status) {
      const map = {
        confirmed: 'Confirmado',
        rescheduled: 'Reagendado',
        cancelled: 'Cancelado',
      };
      return map[status] || '';
    },
    eventAccentColor(calendarId) {
      return this.darkenHex(this.calendarColor(calendarId), 0.25);
    },
    eventCardStyle(calendarId) {
      const baseColor = this.calendarColor(calendarId);
      const accent = this.eventAccentColor(calendarId);
      if (this.colorMode === 'solid') {
        return {
          backgroundColor: baseColor,
          borderLeftColor: accent,
          color: '#ffffff',
        };
      }
      // Subtle: solid muted background (no transparency), dark text
      return {
        backgroundColor: this.hexToMuted(baseColor),
        borderLeftColor: accent,
        color: '#0f172a',
      };
    },
    toggleCalendar(calendar) {
      this.toggleCalendarSelection(calendar.id);
    },
    viewEvent(event) {
      const organizerFlag = event?.is_organizer;
      const shouldBeReadOnly =
        organizerFlag === false || organizerFlag === 'false';

      this.editEventData = { ...event, readOnly: shouldBeReadOnly };
      this.showEventDrawer = true;
    },
    onGridClick(date, event) {
      if (!this.canCreateEvents) return;
      // Don't trigger if clicked on an event card
      if (event.target.closest('[click-stop]')) return;

      // Find where we clicked in terms of hour
      const rect = event.currentTarget.getBoundingClientRect();
      const clickY = event.clientY - rect.top;
      const hourHeight = this.viewMode === 'week' ? 80 : 100;
      const clickedHour = Math.floor(clickY / hourHeight);

      const start = new Date(date);
      start.setHours(clickedHour, 0, 0, 0);

      const end = new Date(start);
      end.setHours(clickedHour + 1, 0, 0, 0);

      this.editEventData = {
        title: '',
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        calendar_id: this.defaultCreatableCalendarId,
      };
      this.showEventDrawer = true;
    },
    onMonthGridClick(date, event) {
      if (!this.canCreateEvents) return;
      if (event.target.closest('[click-stop]')) return;

      const now = new Date();
      const currentHour = now.getHours();

      const start = new Date(date);
      start.setHours(currentHour + 1, 0, 0, 0);

      const end = new Date(start);
      end.setHours(currentHour + 2, 0, 0, 0);

      this.editEventData = {
        title: '',
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        calendar_id: this.defaultCreatableCalendarId,
      };
      this.showEventDrawer = true;
    },
    onDrawerChange(data) {
      if (
        this.editEventData?.readOnly ||
        this.editEventData?.is_organizer === false
      )
        return;
      if (this.editEventData) {
        const attendees = (data.attendeesString || '')
          .split(',')
          .map(email => email.trim())
          .filter(Boolean);
        this.editEventData.metadata = {
          ...(this.editEventData.metadata || {}),
          attendees,
          add_meet_link: !!data.addMeetLink,
          hangout_link: data.hangoutLink || null,
        };

        // Reconstruct start and end dates with the new date and times
        const start = new Date(data.date);
        const [sh, sm] = data.startTime.split(':');
        start.setHours(parseInt(sh, 10), parseInt(sm, 10), 0, 0);

        const end = new Date(data.date);
        const [eh, em] = data.endTime.split(':');
        end.setHours(parseInt(eh, 10), parseInt(em, 10), 0, 0);

        const newStartAt = start.toISOString();
        const newEndAt = end.toISOString();

        // If something changed, update and potentially shift view
        if (
          this.editEventData.start_at !== newStartAt ||
          this.editEventData.end_at !== newEndAt ||
          this.editEventData.calendar_id !== data.calendarId ||
          this.editEventData.title !== data.title ||
          this.editEventData.description !== data.description
        ) {
          this.editEventData.start_at = newStartAt;
          this.editEventData.end_at = newEndAt;
          this.editEventData.calendar_id = data.calendarId;
          this.editEventData.title = data.title;
          this.editEventData.description = data.description;

          // Shift view if new date is not visible
          if (!isSameDay(this.currentDate, start)) {
            // For month view, check if same month
            if (
              this.viewMode === 'month' &&
              !isSameMonth(this.currentDate, start)
            ) {
              this.currentDate = startOfDay(start);
            } else if (this.viewMode === 'week') {
              // For week view, check if within current week
              const weekStart = startOfWeek(this.currentDate);
              const weekEnd = addDays(weekStart, 6);
              if (start < weekStart || start > weekEnd) {
                this.currentDate = startOfDay(start);
              }
            } else if (this.viewMode === 'day') {
              this.currentDate = startOfDay(start);
            }
          }
        }
      }
    },
    async onEventSave(eventData) {
      if (this.isSavingEvent) return;
      this.isSavingEvent = true;
      try {
        if (eventData.id) {
          await this.$store.dispatch('calendar/updateEvent', eventData);
        } else {
          await this.$store.dispatch('calendar/createEvent', eventData);
        }
        this.showEventDrawer = false;
        useAlert(this.$t('CALENDAR.EVENT_SAVE_SUCCESS'));
      } catch (error) {
        const message =
          error?.response?.data?.error || this.$t('CALENDAR.EVENT_SAVE_ERROR');
        useAlert(message);
      } finally {
        this.isSavingEvent = false;
      }
    },
    async onEventDelete(eventId) {
      if (this.isSavingEvent) return;
      this.isSavingEvent = true;
      try {
        await this.$store.dispatch('calendar/deleteEvent', eventId);
        this.showEventDrawer = false;
        useAlert(this.$t('CALENDAR.EVENT_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CALENDAR.EVENT_DELETE_ERROR'));
      } finally {
        this.isSavingEvent = false;
      }
    },
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      const dy = e.changedTouches[0].clientY - this.touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        this.currentDate = addDays(this.currentDate, dx < 0 ? 1 : -1);
      }
    },
    openNewEventModal() {
      this.onGridClick(new Date(), {
        currentTarget: { getBoundingClientRect: () => ({ top: 0 }) },
        clientY: 10 * 80,
      }); // Default to 10am
    },
    connectGoogle() {
      const state = btoa(
        JSON.stringify({
          account_id: this.accountId,
          user_id: this.$store.getters.getCurrentUser.id,
        })
      );
      window.location.href = `/google_calendar/authorize?state=${state}`;
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col flex-1 h-full overflow-hidden bg-n-surface-1"
  >
    <!-- Header -->
    <header
      class="sticky top-0 z-30 border-b border-n-weak bg-n-surface-1 flex-shrink-0"
    >
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between w-full py-4 px-4 sm:px-6 gap-3 sm:gap-2"
      >
        <div
          class="w-full sm:w-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
        >
          <h1 class="text-lg sm:text-xl font-bold text-n-slate-12">
            {{ $t('CALENDAR.TITLE') }}
          </h1>
          <div
            class="flex items-center gap-1 bg-n-alpha-1 rounded-lg p-1 w-full sm:w-auto"
          >
            <WuiButton
              variant="faded"
              size="sm"
              icon="i-lucide-chevron-left"
              @click="shiftPeriod(-1)"
            />
            <WuiButton
              variant="faded"
              size="sm"
              class="px-3 flex-1 sm:flex-initial"
              @click="goToToday"
            >
              {{ $t('CALENDAR.TODAY') }}
            </WuiButton>
            <WuiButton
              variant="faded"
              size="sm"
              icon="i-lucide-chevron-right"
              @click="shiftPeriod(1)"
            />
          </div>
          <span class="text-sm sm:text-lg font-medium text-n-slate-11 sm:ml-2">
            {{ currentPeriodLabel }}
          </span>
        </div>

        <div class="flex items-center gap-2 w-full sm:w-auto">
          <div
            class="flex bg-n-alpha-1 rounded-lg p-1 sm:mr-2 flex-1 sm:flex-initial"
          >
            <button
              v-for="v in viewOptions"
              :key="v.id"
              class="px-3 py-1 text-xs font-medium rounded-md transition-all flex-1 sm:flex-initial"
              :class="
                viewMode === v.id
                  ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="viewMode = v.id"
            >
              {{ v.label }}
            </button>
          </div>

          <!-- Header Toolbar Icons -->
          <div class="flex items-center gap-1 border-l border-n-weak pl-2 ml-1">
            <!-- Calendar Selector -->
            <div ref="calendarPopoverAnchor" class="relative">
              <button
                class="flex items-center gap-1 px-2 py-1.5 rounded-lg text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                @click.stop="showCalendarPopover = !showCalendarPopover"
              >
                <span class="i-lucide-calendar size-4" />
                <span
                  class="text-xs font-bold bg-n-alpha-2 rounded-md px-1.5 py-0.5 min-w-[20px] text-center"
                  >{{ selectedCount }}</span
                >
              </button>
              <CalendarPopover
                :show="showCalendarPopover"
                :calendars="allCalendars"
                :connected-accounts="connectedAccounts"
                :can-manage-settings="canManageSettings"
                @toggle="onToggleCalendar"
                @manage="openSettings('accounts')"
              />
            </div>

            <!-- Refresh -->
            <button
              class="flex items-center justify-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
              :class="{ 'animate-spin': isSyncing }"
              :disabled="isSyncing"
              @click="triggerSync"
            >
              <span class="i-lucide-refresh-cw size-4" />
            </button>

            <!-- Toggle cancelled events -->
            <button
              class="flex items-center justify-center size-8 rounded-lg transition-colors"
              :class="
                showCancelledEvents
                  ? 'text-red-500 bg-red-50 hover:bg-red-100'
                  : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              :title="
                showCancelledEvents
                  ? $t('CALENDAR.HIDE_CANCELLED')
                  : $t('CALENDAR.SHOW_CANCELLED')
              "
              @click="showCancelledEvents = !showCancelledEvents"
            >
              <span class="i-lucide-ban size-4" />
            </button>

            <!-- Settings -->
            <button
              class="flex items-center justify-center size-8 rounded-lg text-n-slate-11 transition-colors"
              :class="
                canManageSettings
                  ? 'hover:bg-n-alpha-1 hover:text-n-slate-12'
                  : 'opacity-50 cursor-not-allowed'
              "
              :disabled="!canManageSettings"
              @click="canManageSettings && openSettings('appearance')"
            >
              <span class="i-lucide-settings size-4" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Sync in progress banner -->
    <div
      v-if="isSyncing"
      class="flex items-center gap-2 px-4 py-2 bg-n-brand/10 border-b border-n-brand/20 text-n-brand text-sm"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin shrink-0" />
      <span>{{ $t('CALENDAR.SYNC_IN_PROGRESS') }}</span>
    </div>

    <!-- Reauth required banners -->
    <div
      v-for="account in reauthAccounts"
      :key="account.id"
      class="flex items-center gap-2 px-4 py-2 bg-n-ruby-3 border-b border-n-ruby-6 text-n-ruby-11 text-sm"
    >
      <span class="i-lucide-alert-triangle size-4 shrink-0" />
      <span class="flex-1">
        {{ $t('CALENDAR.REAUTH_REQUIRED') || `Conexão expirada para ${account.email}. Reconecte para sincronizar.` }}
      </span>
      <button
        class="text-xs font-medium underline hover:no-underline"
        @click="connectGoogle"
      >
        {{ $t('CALENDAR.RECONNECT') || 'Reconectar' }}
      </button>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Grid do Calendário -->
      <main class="flex-1 min-h-0 overflow-auto bg-n-surface-1 p-3 sm:p-6">
        <!-- Month View -->
        <div
          v-if="viewMode === 'month'"
          class="h-full min-h-0 flex flex-col border border-n-weak rounded-xl overflow-hidden shadow-sm"
        >
          <div class="grid grid-cols-7 bg-n-surface-2 border-b border-n-weak">
            <div
              v-for="day in weekDayLabels"
              :key="day"
              class="py-2 text-center text-xs font-semibold text-n-slate-11 uppercase"
            >
              {{ day }}
            </div>
          </div>
          <div
            class="flex-1 min-h-0 grid grid-cols-7 bg-n-surface-1 auto-rows-[minmax(0,1fr)]"
            :class="monthDays.length > 35 ? 'grid-rows-6' : 'grid-rows-5'"
          >
            <div
              v-for="(day, idx) in monthDays"
              :key="idx"
              class="border-r border-b border-n-weak p-1.5 sm:p-2 flex flex-col gap-1 min-h-0 sm:min-h-[120px] overflow-hidden transition-colors hover:bg-n-alpha-1/20 cursor-pointer"
              :class="{ 'bg-n-surface-2/30': !day.isCurrentMonth }"
              @click="onMonthGridClick(day.date, $event)"
            >
              <div class="flex justify-between items-start">
                <span
                  class="size-6 sm:size-7 flex items-center justify-center rounded-full text-xs sm:text-sm font-medium"
                  :class="
                    day.isToday
                      ? 'bg-n-brand text-white'
                      : day.isCurrentMonth
                        ? 'text-n-slate-12'
                        : 'text-n-slate-9'
                  "
                >
                  {{ day.date.getDate() }}
                </span>
              </div>

              <div
                class="space-y-1 overflow-y-auto min-h-0 flex-1 scrollbar-hide"
              >
                <div
                  v-for="event in day.events"
                  :key="event.id"
                  class="px-1.5 sm:px-2 py-0.5 rounded text-[10px] font-medium truncate cursor-pointer transition-transform active:scale-95"
                  :style="{
                    ...eventCardStyle(event.calendar_id),
                    borderLeft: `3px solid ${eventAccentColor(event.calendar_id)}`,
                  }"
                  @click.stop="viewEvent(event)"
                >
                  <span
                    v-if="event.status && event.status !== 'pending'"
                    :class="eventStatusDot(event.status)"
                    class="inline-block size-1.5 rounded-full mr-1 shrink-0 align-middle"
                  />{{ formatTime(event.start_at) }} {{ event.title }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Week View -->
        <div
          v-else-if="viewMode === 'week'"
          class="h-full min-h-0 flex flex-col border border-n-weak rounded-xl overflow-hidden shadow-sm"
        >
          <!-- MOBILE: compact day strip + single-day swipeable grid -->
          <template v-if="isMobileViewport">
            <div
              class="flex bg-n-surface-2 border-b border-n-weak flex-shrink-0"
            >
              <div
                v-for="day in weekDates"
                :key="day.date.getTime()"
                class="flex-1 flex flex-col items-center py-2 cursor-pointer transition-colors"
                :class="isSameDay(day.date, currentDate) ? 'bg-n-alpha-2' : ''"
                @click="currentDate = new Date(day.date)"
              >
                <span
                  class="text-[9px] font-semibold uppercase leading-tight"
                  :class="
                    isSameDay(day.date, currentDate)
                      ? 'text-n-brand'
                      : 'text-n-slate-11'
                  "
                  >{{ day.label }}</span
                >
                <span
                  class="mt-0.5 size-7 flex items-center justify-center rounded-full text-sm font-bold"
                  :class="
                    day.isToday
                      ? 'bg-n-brand text-white'
                      : isSameDay(day.date, currentDate)
                        ? 'text-n-brand'
                        : 'text-n-slate-12'
                  "
                  >{{ day.date.getDate() }}</span
                >
                <span
                  class="size-1 rounded-full mt-0.5"
                  :class="
                    getEventsForDate(day.date).length ||
                    getAllDayEventsForDate(day.date).length
                      ? isSameDay(day.date, currentDate) || day.isToday
                        ? 'bg-n-brand'
                        : 'bg-n-slate-9'
                      : 'bg-transparent'
                  "
                />
              </div>
            </div>

            <div
              v-if="getAllDayEventsForDate(currentDate).length"
              class="px-3 py-1.5 border-b border-n-weak bg-n-surface-2 flex items-start gap-2 flex-shrink-0"
            >
              <span
                class="text-[9px] text-n-slate-9 uppercase font-semibold pt-0.5 flex-shrink-0 leading-tight"
                v-html="$t('CALENDAR.ALL_DAY')"
              />
              <div class="flex flex-wrap gap-1">
                <div
                  v-for="event in getAllDayEventsForDate(currentDate)"
                  :key="event.id"
                  class="px-2 py-0.5 rounded text-[10px] font-medium cursor-pointer truncate max-w-[200px] transition-transform active:scale-95"
                  :style="{
                    ...eventCardStyle(event.calendar_id),
                    borderLeft: `3px solid ${eventAccentColor(event.calendar_id)}`,
                  }"
                  @click.stop="viewEvent(event)"
                >
                  {{ event.title }}
                </div>
              </div>
            </div>

            <div
              class="flex-1 min-h-0 overflow-y-auto scrollbar-hide relative bg-n-surface-1"
              @touchstart.passive="onTouchStart"
              @touchend.passive="onTouchEnd"
            >
              <div class="flex">
                <div
                  class="w-14 flex-shrink-0 border-r border-n-weak bg-n-surface-2"
                >
                  <div
                    v-for="hour in hours"
                    :key="hour"
                    class="h-[80px] relative"
                  >
                    <span
                      class="absolute -top-3 left-0 w-full text-center text-[10px] font-medium text-n-slate-9 uppercase"
                    >
                      {{ formatHour(hour) }}
                    </span>
                  </div>
                </div>
                <div
                  class="flex-1 relative cursor-pointer"
                  @click="onGridClick(currentDate, $event)"
                >
                  <div
                    v-if="isToday(currentDate)"
                    class="absolute left-0 right-0 z-20 pointer-events-none"
                    :style="{ top: `${getCurrentTimeTop()}px` }"
                  >
                    <div class="relative w-full border-t-2 border-n-ruby-9">
                      <div
                        class="absolute -left-10 -top-2 bg-n-ruby-9 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                      >
                        {{ format(currentTime, 'H:mm') }}
                      </div>
                      <div
                        class="absolute -left-1 -top-1 size-2 rounded-full bg-n-ruby-9 ring-2 ring-white"
                      />
                    </div>
                  </div>
                  <div
                    v-for="hour in hours"
                    :key="hour"
                    class="h-[80px] border-b border-n-weak last:border-0"
                  />
                  <div class="absolute inset-0">
                    <div
                      v-for="event in getEventsForDate(currentDate)"
                      :key="event.id"
                      class="px-2 py-1 m-1 rounded-lg text-xs font-semibold shadow-sm border-l-4 transition-all cursor-pointer overflow-hidden"
                      :class="
                        event.isTemp
                          ? 'opacity-50 border-dashed animate-pulse'
                          : ''
                      "
                      :style="getEventStyle(event)"
                      click-stop
                      @click.stop="viewEvent(event)"
                    >
                      <div class="flex flex-col h-full">
                        <span class="text-[9px] opacity-80 leading-tight"
                          >{{ formatTime(event.start_at) }} -
                          {{ formatTime(event.end_at) }}</span
                        >
                        <span
                          class="truncate leading-tight flex items-center gap-1"
                        >
                          <span
                            v-if="event.status && event.status !== 'pending'"
                            :class="eventStatusDot(event.status)"
                            class="inline-block size-1.5 rounded-full shrink-0"
                          />
                          {{ event.title }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- DESKTOP: 7-day header + scrollable grid with sticky time column -->
          <template v-else>
            <div
              class="flex bg-n-surface-2 border-b border-n-weak min-w-[720px]"
            >
              <div class="w-16 flex-shrink-0 border-r border-n-weak" />
              <div class="flex-1 grid grid-cols-7">
                <div
                  v-for="day in weekDates"
                  :key="day.date.getTime()"
                  class="py-3 text-center border-r border-n-weak last:border-r-0"
                >
                  <span
                    class="block text-xs font-semibold text-n-slate-11 uppercase"
                    >{{ day.label }}</span
                  >
                  <span
                    class="inline-flex size-8 mt-1 items-center justify-center rounded-full text-lg font-bold"
                    :class="
                      day.isToday ? 'bg-n-brand text-white' : 'text-n-slate-12'
                    "
                    >{{ day.date.getDate() }}</span
                  >
                </div>
              </div>
            </div>

            <div
              v-if="weekDates.some(d => getAllDayEventsForDate(d.date).length)"
              class="flex border-b border-n-weak bg-n-surface-2 min-w-[720px] flex-shrink-0"
            >
              <div
                class="w-16 flex-shrink-0 border-r border-n-weak flex items-center justify-end pr-2 py-1"
              >
                <span
                  class="text-[9px] text-n-slate-9 uppercase leading-tight text-right"
                  v-html="$t('CALENDAR.ALL_DAY')"
                />
              </div>
              <div class="flex-1 grid grid-cols-7">
                <div
                  v-for="(day, idx) in weekDates"
                  :key="idx"
                  class="border-r border-n-weak last:border-r-0 p-1 flex flex-col gap-0.5 min-h-[28px]"
                >
                  <div
                    v-for="event in getAllDayEventsForDate(day.date)"
                    :key="event.id"
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium truncate cursor-pointer transition-transform active:scale-95"
                    :style="{
                      ...eventCardStyle(event.calendar_id),
                      borderLeft: `3px solid ${eventAccentColor(event.calendar_id)}`,
                    }"
                    @click.stop="viewEvent(event)"
                  >
                    {{ event.title }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex-1 min-h-0 overflow-auto scrollbar-hide relative bg-n-surface-1"
            >
              <div class="flex min-w-[720px]">
                <div
                  class="w-16 flex-shrink-0 border-r border-n-weak bg-n-surface-2 sticky left-0 z-10"
                >
                  <div
                    v-for="hour in hours"
                    :key="hour"
                    class="h-[80px] relative"
                  >
                    <span
                      class="absolute -top-3 left-0 w-full text-center text-[10px] font-medium text-n-slate-9 uppercase"
                    >
                      {{ formatHour(hour) }}
                    </span>
                  </div>
                </div>
                <div class="flex-1 grid grid-cols-7 relative">
                  <div
                    v-for="(day, idx) in weekDates"
                    :key="idx"
                    class="border-r border-n-weak last:border-r-0 relative cursor-pointer"
                    @click="onGridClick(day.date, $event)"
                  >
                    <div
                      v-if="day.isToday"
                      class="absolute left-0 right-0 z-20 pointer-events-none"
                      :style="{ top: `${getCurrentTimeTop()}px` }"
                    >
                      <div class="relative w-full border-t-2 border-n-ruby-9">
                        <div
                          class="absolute -left-12 -top-2 bg-n-ruby-9 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
                        >
                          {{ format(currentTime, 'H:mm') }}
                        </div>
                        <div
                          class="absolute -left-1 -top-1 size-2 rounded-full bg-n-ruby-9 ring-2 ring-white"
                        />
                      </div>
                    </div>
                    <div
                      v-for="hour in hours"
                      :key="hour"
                      class="h-[80px] border-b border-n-weak last:border-0"
                    />
                    <div class="absolute inset-0">
                      <div
                        v-for="event in getEventsForDate(day.date)"
                        :key="event.id"
                        class="px-2 py-1 m-1 rounded-lg text-xs font-semibold shadow-sm border-l-4 transition-all hover:brightness-95 cursor-pointer overflow-hidden group"
                        :class="
                          event.isTemp
                            ? 'opacity-50 border-dashed animate-pulse'
                            : ''
                        "
                        :style="getEventStyle(event)"
                        click-stop
                        @click.stop="viewEvent(event)"
                      >
                        <div class="flex flex-col h-full">
                          <span class="text-[9px] opacity-80 leading-tight"
                            >{{ formatTime(event.start_at) }} -
                            {{ formatTime(event.end_at) }}</span
                          >
                          <span
                            class="truncate leading-tight flex items-center gap-1"
                          >
                            <span
                              v-if="event.status && event.status !== 'pending'"
                              :class="eventStatusDot(event.status)"
                              class="inline-block size-1.5 rounded-full shrink-0"
                            />
                            {{ event.title }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Day View -->
        <div
          v-else-if="viewMode === 'day'"
          class="h-full min-h-0 flex flex-col bg-n-surface-1 border border-n-weak rounded-xl shadow-sm overflow-hidden"
        >
          <div
            class="p-4 sm:p-6 border-b border-n-weak bg-n-surface-2 flex items-center justify-between"
          >
            <div class="flex items-center gap-4">
              <span class="text-2xl sm:text-3xl font-bold text-n-slate-12">{{
                format(currentDate, 'd')
              }}</span>
              <div>
                <h2 class="text-base sm:text-lg font-bold text-n-slate-12">
                  {{ format(currentDate, 'EEEE', { locale: ptBR }) }}
                </h2>
                <p
                  class="text-n-slate-11 text-xs uppercase tracking-wider font-semibold"
                >
                  {{ format(currentDate, 'MMMM yyyy', { locale: ptBR }) }}
                </p>
              </div>
            </div>
            <div
              v-if="isToday(currentDate)"
              class="px-3 py-1 bg-n-brand text-white text-[10px] font-bold rounded-full shadow-sm"
            >
              {{ $t('CALENDAR.TODAY') }}
            </div>
          </div>

          <!-- All-day row (holidays, full-day events) -->
          <div
            v-if="getAllDayEventsForDate(currentDate).length"
            class="px-4 sm:px-6 py-2 border-b border-n-weak bg-n-surface-2 flex items-start gap-3 flex-shrink-0"
          >
            <span
              class="text-[9px] text-n-slate-9 uppercase font-semibold pt-1 flex-shrink-0 leading-tight"
              v-html="$t('CALENDAR.ALL_DAY')"
            />
            <div class="flex flex-wrap gap-1.5">
              <div
                v-for="event in getAllDayEventsForDate(currentDate)"
                :key="event.id"
                class="px-2.5 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-transform active:scale-95"
                :style="{
                  ...eventCardStyle(event.calendar_id),
                  borderLeft: `3px solid ${eventAccentColor(event.calendar_id)}`,
                }"
                @click.stop="viewEvent(event)"
              >
                {{ event.title }}
              </div>
            </div>
          </div>

          <div class="flex-1 min-h-0 overflow-y-auto scrollbar-hide relative">
            <div class="flex">
              <!-- Time column -->
              <div
                class="w-20 flex-shrink-0 border-r border-n-weak bg-n-surface-2 p-4"
              >
                <div
                  v-for="hour in hours"
                  :key="hour"
                  class="h-[100px] relative"
                >
                  <span
                    class="absolute -top-3 left-0 w-full text-center text-xs font-semibold text-n-slate-11 uppercase"
                  >
                    {{ formatHour(hour) }}
                  </span>
                </div>
              </div>

              <!-- Content Area -->
              <div
                class="flex-1 relative bg-n-surface-1 cursor-pointer"
                @click="onGridClick(currentDate, $event)"
              >
                <!-- Current Time Indicator -->
                <div
                  v-if="isToday(currentDate)"
                  class="absolute left-0 right-0 z-20 pointer-events-none"
                  :style="{ top: `${getCurrentTimeTop_Day()}px` }"
                >
                  <div class="relative w-full border-t-2 border-n-ruby-9">
                    <div
                      class="absolute -left-16 -top-2.5 bg-n-ruby-9 text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-md"
                    >
                      {{ format(currentTime, 'H:mm') }}
                    </div>
                    <div
                      class="absolute -left-1.5 -top-1.5 size-3 rounded-full bg-n-ruby-9 ring-2 ring-white"
                    />
                  </div>
                </div>

                <!-- Grid lines -->
                <div
                  v-for="hour in hours"
                  :key="hour"
                  class="h-[100px] border-b border-n-weak last:border-0"
                />

                <!-- Events Overlay -->
                <div class="absolute inset-0">
                  <div
                    v-for="event in getEventsForDate(currentDate)"
                    :key="event.id"
                    class="mx-2 sm:mx-4 my-1 p-3 sm:p-4 rounded-xl text-sm font-bold shadow-md border-l-8 transition-all hover:brightness-95 cursor-pointer group overflow-hidden"
                    :class="
                      event.isTemp
                        ? 'opacity-50 border-dashed animate-pulse'
                        : ''
                    "
                    :style="getEventStyle_Day(event)"
                    click-stop
                    @click.stop="viewEvent(event)"
                  >
                    <div class="flex flex-col h-full gap-1">
                      <div
                        class="flex items-center justify-between opacity-80 text-xs"
                      >
                        <span
                          >{{ formatTime(event.start_at) }} -
                          {{ formatTime(event.end_at) }}</span
                        >
                        <div class="flex items-center gap-1.5">
                          <span
                            v-if="event.status && event.status !== 'pending'"
                            :class="eventStatusBadgeClass(event.status)"
                            class="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                            >{{ eventStatusLabel(event.status) }}</span
                          >
                          <span
                            v-if="event.location"
                            class="i-lucide-map-pin size-3"
                          />
                        </div>
                      </div>
                      <div class="text-base truncate">{{ event.title }}</div>
                      <div
                        v-if="event.description"
                        class="text-xs font-medium opacity-70 line-clamp-2 mt-2"
                      >
                        {{ event.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Event Management Drawer -->
    <EventDrawer
      :show="showEventDrawer"
      :initial-data="editEventData"
      :is-saving="isSavingEvent"
      @close="showEventDrawer = false"
      @change="onDrawerChange"
      @save="onEventSave"
      @delete="onEventDelete"
    />

    <!-- Settings Modal -->
    <CalendarSettingsModal
      :show="showSettingsModal"
      :account-id="accountId"
      :connected-accounts="connectedAccounts"
      :all-calendars="allCalendars"
      :all-inboxes="allInboxes"
      :color-mode="colorMode"
      :default-calendar-id="defaultCalendarId"
      :initial-tab="settingsTab"
      @close="showSettingsModal = false"
      @connect="connectGoogle"
      @disconnect="onDisconnectAccount"
      @sync-account="onSyncAccount"
      @update:color-mode="onColorModeChange"
      @set-default="onDefaultCalendarChange"
      @calendar-updated="onCalendarUpdated"
    />
  </div>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
